import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Link,
  Stack,
  Chip,
} from "@mui/material";
import { Close, Cookie, Settings } from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";

interface CookieConsentProps {
  onAccept: () => void;
  onDecline: () => void;
  onSettings: () => void;
}

const CookieConsent: React.FC<CookieConsentProps> = ({
  onAccept,
  onDecline,
  onSettings,
}) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Check if user has already made a choice
    const hasConsented = localStorage.getItem("cookieConsent");
    if (!hasConsented) {
      // Show banner after a short delay
      const timer = setTimeout(() => setIsVisible(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem("cookieConsent", "accepted");
    setIsVisible(false);
    onAccept();
  };

  const handleDecline = () => {
    localStorage.setItem("cookieConsent", "declined");
    setIsVisible(false);
    onDecline();
  };

  const handleSettings = () => {
    setIsVisible(false);
    onSettings();
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Box
            sx={{
              position: "fixed",
              bottom: 0,
              left: 0,
              right: 0,
              zIndex: 9999,
              p: 2,
              bgcolor: "rgba(0,0,0,0.8)",
            }}
          >
            <Paper
              sx={{
                p: 3,
                borderRadius: 2,
                maxWidth: 1200,
                mx: "auto",
                position: "relative",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 2,
                  flexWrap: "wrap",
                }}
              >
                <Box sx={{ flex: 1, minWidth: 300 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <Cookie sx={{ mr: 1, color: "primary.main" }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      We use cookies to enhance your experience
                    </Typography>
                  </Box>

                  <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.6 }}>
                    Dad2Kid uses cookies and similar technologies to provide,
                    protect, and improve our service. By clicking "Accept All",
                    you consent to our use of cookies for analytics,
                    personalization, and to remember your preferences.
                  </Typography>

                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ mb: 2, flexWrap: "wrap" }}
                  >
                    <Chip
                      label="Essential"
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                    <Chip
                      label="Analytics"
                      size="small"
                      color="secondary"
                      variant="outlined"
                    />
                    <Chip
                      label="Personalization"
                      size="small"
                      color="info"
                      variant="outlined"
                    />
                  </Stack>

                  <Typography variant="body2" color="text.secondary">
                    Learn more in our{" "}
                    <Link
                      href="#privacy"
                      sx={{ textDecoration: "none", fontWeight: 600 }}
                    >
                      Privacy Policy
                    </Link>{" "}
                    and{" "}
                    <Link
                      href="#cookies"
                      sx={{ textDecoration: "none", fontWeight: 600 }}
                    >
                      Cookie Policy
                    </Link>
                  </Typography>
                </Box>

                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1}
                  sx={{ flexShrink: 0 }}
                >
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Settings />}
                    onClick={handleSettings}
                    sx={{ minWidth: 120 }}
                  >
                    Settings
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleDecline}
                    sx={{ minWidth: 120 }}
                  >
                    Decline
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleAccept}
                    sx={{ minWidth: 120 }}
                  >
                    Accept All
                  </Button>
                </Stack>

                <Button
                  size="small"
                  onClick={handleDecline}
                  sx={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    minWidth: "auto",
                    p: 0.5,
                  }}
                >
                  <Close />
                </Button>
              </Box>
            </Paper>
          </Box>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieConsent;
